import * as React from 'react';
import styled from 'styled-components';
import {transparentize} from 'polished';
import {
  Column,
  BlockTitle,
  BlockLabel as Label,
} from '../components/styled';
import {transition, visuallyHidden} from 'helpers/css';
import PlusRound from 'icons/PlusRound';
import {QuestionItem} from '../types';
import {CoursePromoContext} from '../context';

const Container = styled.div`
  width: 100%;
  display: flex;
  padding: 0 30px 0 60px;
  
  ${Column} {
    width: 100%;
  }

  @media screen and (max-width: 1280px) {
    width: auto;
  }

  @media screen and (max-width: 640px) {
    padding: 0 16px;
  }
`;

const Title = styled(BlockTitle)`
  max-width: 298px;
  margin-bottom: 40px;

  @media screen and (max-width: 640px) {
    margin-bottom: 20px;
  }
`;

const Item = styled.div`
  padding: 26px 0;
  width: 100%;
  max-width: 761px;
  
  border: 1px solid ${({theme}) => (
    transparentize(.85, theme.colors.white)
  )};
  border-left: none;
  border-right: none;

  @media screen and (max-width: 1280px) {
    padding: 20px 0;
  }
`;

const ItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  cursor: pointer;
`;

const Content = styled.div`
  margin-left: auto;
  width: 100%;
  max-width: 760px;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  
  ${Title} {
    margin-right: 101px;
  }

  @media screen and (max-width: 1280px) {
    flex-wrap: wrap;
    
    ${Title} {
      margin-right: 0;
      max-width: none;
    }
  }
`;

const Question = styled.div`
  font-size: 18px;
  font-weight: 600;
  line-height: 23px;

  @media screen and (max-width: 1280px) {
    font-size: 16px;
    line-height: 21px;
  }
`;

const Answer = styled.div`
  max-width: 662px;
  max-height: 0;
  margin-top: 0;
  line-height: 24px;
  overflow: hidden;

  color: ${({theme}) => (
    transparentize(.2, theme.colors.white)
  )};
  
  ${transition()};
`;

const Toggle = styled.div`
  width: 22px;
  height: 22px;
  
  background: ${({theme}) => `
    ${theme.variables.backgroundColor};
  `};
  
  border-radius: 50%;
  transform: rotate(0deg);
  cursor: pointer;
  
  ${transition()}
`;

const Checker = styled.label`
  &:hover ${Toggle} {
    background: ${({theme}) => `
      ${theme.colors.white};
    `};
    
    svg {
      path:last-child {
        fill: ${({theme}) => `
          ${theme.variables.backgroundColor};
        `};
      }
    }
  }
`;

const HiddenChecker = styled.input`
  ${visuallyHidden};
  
  &:checked {
    & + ${Checker} > ${ItemHeader} {
      ${Toggle} {
        transform: rotate(45deg);
      }
    }
    & + ${Checker} + ${Answer} {
      margin-top: 14px;
      max-height: 335px;
    }
  }
`;


/**
 * The returns course frequently questions component
 * @return {React.ReactElement}
 * @constructor
 */
export default function FrequentlyQuestions(): React.ReactElement{
  const {questions} = React.useContext(CoursePromoContext);

  /**
   * Render questions items
   * @constructor
   */
  function renderContent() {
    return questions.items.map(renderItem);
  }

  /**
   * Render course questions item
   * @param {QuestionItem} item
   * @param {number} ind
   * @constructor
   */
  function renderItem(item: QuestionItem, ind: number) {
    const id = 'checker_' + ind;
    return (
      <Item key={ind + item.toString()}>
        <HiddenChecker type='checkbox' id={id} />
        <Checker htmlFor={id}>
          <ItemHeader>
            <Question>{item.question}</Question>
            <Toggle>
              <PlusRound />
            </Toggle>
          </ItemHeader>
        </Checker>
        <Answer>
          {item.answer}
        </Answer>
      </Item>
    );
  }

  return (
    <Container>
      <Column>
        <Label>вопрос-ответ</Label>
        <Row>
          <Title>
            {questions.title}
          </Title>
          <Content>
            {renderContent()}
          </Content>
        </Row>
      </Column>
    </Container>
  );
}
